<template>
  <div class="bulk-preview">
    <div class="mb-6">
      <h4 class="text-lg font-medium text-slate-900 dark:text-white mb-2">
        {{ $t('BULK_BROADCAST.PREVIEW.TITLE') }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('BULK_BROADCAST.PREVIEW.DESCRIPTION') }}
      </p>
    </div>

    <!-- Summary Stats -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div class="flex items-center">
          <Icon icon="i-lucide-users" class="text-blue-500 mr-3" size="24" />
          <div>
            <p class="text-sm font-medium text-blue-600 dark:text-blue-400">
              {{ $t('BULK_BROADCAST.PREVIEW.TOTAL_RECIPIENTS') }}
            </p>
            <p class="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {{ totalRecipients }}
            </p>
          </div>
        </div>
      </div>

      <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div class="flex items-center">
          <Icon icon="i-lucide-message-circle" class="text-green-500 mr-3" size="24" />
          <div>
            <p class="text-sm font-medium text-green-600 dark:text-green-400">
              {{ $t('BULK_BROADCAST.PREVIEW.CHANNEL') }}
            </p>
            <p class="text-lg font-bold text-green-900 dark:text-green-100 capitalize">
              {{ channelDisplayName }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="template" class="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
        <div class="flex items-center">
          <Icon icon="i-lucide-file-text" class="text-purple-500 mr-3 flex-shrink-0" size="24" />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-purple-600 dark:text-purple-400">
              Plantilla
            </p>
            <p class="text-lg font-bold text-purple-900 dark:text-purple-100 truncate" :title="template.name">
              {{ template.name }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="hasAssignment" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div class="flex items-center">
          <Icon icon="i-lucide-user-check" class="text-amber-500 mr-3 flex-shrink-0" size="24" />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-amber-600 dark:text-amber-400">
              Asignación
            </p>
            <p class="text-sm font-bold text-amber-900 dark:text-amber-100 truncate" :title="assignmentLabel">
              {{ assignmentLabel }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Assignment Details (if configured) -->
    <div v-if="hasAssignment" class="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <h6 class="font-medium text-slate-900 dark:text-white mb-3 flex items-center">
        <Icon icon="i-lucide-info" class="mr-2" size="16" />
        Configuración de Asignación
      </h6>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div v-if="assignedTeam">
          <p class="text-slate-600 dark:text-slate-400 mb-1">Equipo asignado:</p>
          <p class="font-semibold text-slate-900 dark:text-white flex items-center">
            <Icon icon="i-lucide-users" class="mr-2 text-blue-600" size="16" />
            {{ assignedTeam.name }}
          </p>
        </div>
        <div v-if="assignedAgent">
          <p class="text-slate-600 dark:text-slate-400 mb-1">Agente asignado:</p>
          <p class="font-semibold text-slate-900 dark:text-white flex items-center">
            <Icon icon="i-lucide-user" class="mr-2 text-purple-600" size="16" />
            {{ assignedAgent.name }}
          </p>
        </div>
      </div>
      <p class="mt-3 text-xs text-slate-600 dark:text-slate-400 italic">
        ℹ️ Todas las conversaciones {{ assignedTeam ? 'nuevas o existentes' : 'creadas' }} se asignarán automáticamente {{ assignmentAction }}.
      </p>
    </div>

    <!-- Message Preview Sample -->
    <div v-if="sampleMessage" class="mb-6 p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
      <h6 class="font-medium text-slate-900 dark:text-white mb-3">
        Ejemplo de mensaje
      </h6>
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 max-w-md">
        <div class="flex items-start space-x-3">
          <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon icon="i-lucide-message-circle" class="text-white" size="16" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm text-slate-900 dark:text-white whitespace-pre-line break-words">
              {{ sampleMessage }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-2 truncate" :title="sampleRecipientFull">
              Para: {{ sampleRecipient }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Recipients Table -->
    <div class="mb-6">
      <div class="flex items-center justify-between mb-4">
        <h6 class="font-medium text-slate-900 dark:text-white">
          {{ $t('BULK_BROADCAST.PREVIEW.RECIPIENTS_LIST') }}
        </h6>
        <div class="flex items-center space-x-4">
          <span class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('BULK_BROADCAST.PREVIEW.SHOWING_RECIPIENTS', { shown: Math.min(previewData.rows.length, 20), total: previewData.rows.length }) }}
          </span>
          <button
            v-if="showAllRows"
            @click="showAllRows = false"
            class="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {{ $t('BULK_BROADCAST.PREVIEW.SHOW_LESS') }}
          </button>
          <button
            v-else-if="previewData.rows.length > 20"
            @click="showAllRows = true"
            class="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {{ $t('BULK_BROADCAST.PREVIEW.SHOW_ALL') }}
          </button>
        </div>
      </div>

      <div class="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
        <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead class="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                #
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ $t('BULK_BROADCAST.PREVIEW.PHONE') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ $t('BULK_BROADCAST.PREVIEW.NAME') }}
              </th>
              <th v-if="hasVariables" class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ $t('BULK_BROADCAST.PREVIEW.VARIABLES') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ $t('BULK_BROADCAST.PREVIEW.MESSAGE') }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
            <tr
              v-for="(row, index) in displayedRows"
              :key="index"
              :class="index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'"
            >
              <td class="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-3 text-sm text-slate-900 dark:text-white font-mono">
                {{ row.phone }}
              </td>
              <td class="px-4 py-3 text-sm text-slate-900 dark:text-white">
                {{ row.name }}
              </td>
              <td v-if="hasVariables" class="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="(variable, varIndex) in row.variablesUsed"
                    :key="varIndex"
                    class="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                  >
                    {{ variable }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-slate-900 dark:text-white">
                <div class="max-w-sm">
                  <p
                    v-if="row.renderedMessage.length > 100"
                    v-tooltip.top="row.renderedMessage"
                    class="truncate cursor-help"
                  >
                    {{ row.renderedMessage }}
                  </p>
                  <p v-else class="whitespace-pre-line">{{ row.renderedMessage }}</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Duplicate Phone Warning -->
    <div v-if="duplicatePhones.length > 0" class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-alert-triangle" class="text-yellow-500 mt-0.5" size="20" />
        <div>
          <h6 class="font-medium text-yellow-900 dark:text-yellow-100">
            {{ $t('BULK_BROADCAST.PREVIEW.DUPLICATE_PHONES_TITLE') }}
          </h6>
          <p class="mt-1 text-sm text-yellow-800 dark:text-yellow-200 mb-3">
            {{ $t('BULK_BROADCAST.PREVIEW.DUPLICATE_PHONES_DESCRIPTION', { count: duplicatePhones.length }) }}
          </p>
          <div class="text-sm font-mono text-yellow-700 dark:text-yellow-300">
            {{ duplicatePhones.slice(0, 5).join(', ') }}
            <span v-if="duplicatePhones.length > 5">
              {{ $t('BULK_BROADCAST.PREVIEW.AND_MORE', { count: duplicatePhones.length - 5 }) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Final Summary -->
    <div class="p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div class="flex items-center justify-between">
        <div>
          <h6 class="font-medium text-slate-900 dark:text-white mb-2">
            {{ $t('BULK_BROADCAST.PREVIEW.READY_TO_SEND') }}
          </h6>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ getFinalSummaryText() }}
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <Icon icon="i-lucide-send" class="text-green-500" size="24" />
          <span class="text-2xl font-bold text-green-600 dark:text-green-400">
            {{ totalRecipients }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  previewData: {
    type: Object,
    required: true
  },
  channel: {
    type: String,
    required: true
  },
  template: {
    type: Object,
    default: null
  },
  assignment: {
    type: Object,
    default: () => ({ teamId: null, agentId: null })
  }
});

defineEmits(['update:preview']);

const { t } = useI18n();

const showAllRows = ref(false);

// Store para obtener teams y agents
const store = useStore();
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');

const totalRecipients = computed(() => {
  return props.previewData.rows?.length || 0;
});

const channelDisplayName = computed(() => {
  const names = {
    whatsapp: 'WhatsApp',
    email: 'Email',
    sms: 'SMS'
  };
  return names[props.channel] || props.channel;
});

const hasVariables = computed(() => {
  return props.previewData.rows?.some(row => row.variablesUsed?.length > 0) || false;
});

const displayedRows = computed(() => {
  if (!props.previewData.rows) return [];
  return showAllRows.value ? props.previewData.rows : props.previewData.rows.slice(0, 20);
});

// Assignment computed properties
const hasAssignment = computed(() => {
  return props.assignment && (props.assignment.teamId || props.assignment.agentId);
});

const assignedTeam = computed(() => {
  if (!props.assignment?.teamId) return null;
  return teams.value.find(t => t.id === props.assignment.teamId);
});

const assignedAgent = computed(() => {
  if (!props.assignment?.agentId) return null;
  return agents.value.find(a => a.id === props.assignment.agentId);
});

const assignmentLabel = computed(() => {
  const parts = [];
  if (assignedTeam.value) {
    parts.push(assignedTeam.value.name);
  }
  if (assignedAgent.value) {
    parts.push(assignedAgent.value.name);
  }
  return parts.length > 0 ? parts.join(' / ') : 'Sin asignación';
});

const assignmentAction = computed(() => {
  const parts = [];
  if (assignedTeam.value) {
    parts.push(`al equipo "${assignedTeam.value.name}"`);
  }
  if (assignedAgent.value) {
    parts.push(`al agente "${assignedAgent.value.name}"`);
  }
  return parts.join(' y ');
});

const sampleMessage = computed(() => {
  return props.previewData.rows?.[0]?.renderedMessage || '';
});

const sampleRecipient = computed(() => {
  const firstRow = props.previewData.rows?.[0];
  return firstRow ? `${firstRow.name} (${firstRow.phone})` : '';
});

const sampleRecipientFull = computed(() => {
  const firstRow = props.previewData.rows?.[0];
  return firstRow ? `${firstRow.name} (${firstRow.phone})` : '';
});

const duplicatePhones = computed(() => {
  if (!props.previewData.rows) return [];
  
  const phoneCount = {};
  const duplicates = [];
  
  props.previewData.rows.forEach(row => {
    const phone = row.phone;
    phoneCount[phone] = (phoneCount[phone] || 0) + 1;
  });
  
  Object.keys(phoneCount).forEach(phone => {
    if (phoneCount[phone] > 1) {
      duplicates.push(phone);
    }
  });
  
  return duplicates;
});

const getFinalSummaryText = () => {
  const channel = channelDisplayName.value;
  const count = totalRecipients.value;
  
  if (props.template) {
    return t('BULK_BROADCAST.PREVIEW.FINAL_SUMMARY_WITH_TEMPLATE', {
      count,
      channel,
      template: props.template.name
    });
  } else {
    return t('BULK_BROADCAST.PREVIEW.FINAL_SUMMARY', {
      count,
      channel
    });
  }
};
</script>